
import type React from "react"
import { useEffect, useState } from "react"
import CreatePatterns, { type PatternsData } from "./CreatePatterns"
import { buyTicket } from "./apis/buyTicket"
import Timer from "./Timer"
import type { UserProps } from "./apis/user"
import { closeModalData, pageRoutes } from "../App"
import { ticketsProps } from "./apis/getTickets"
import useWindowSize from "./utils/useWindowSize"

interface PurchaseTicketsPageProps {
  drawDetails: any
  onTicketPurchase: () => void
  time: string | Date | undefined
  userWallet: UserProps | undefined
  handleCloseModal: (data: closeModalData) => void
  ticketData: ticketsProps | undefined
  setCurrentPage: React.Dispatch<React.SetStateAction<pageRoutes>>
  getTimer?: boolean
}

const PurchaseTicketsPage: React.FC<PurchaseTicketsPageProps> = ({
  drawDetails,
  userWallet,
  onTicketPurchase,
  time,
  ticketData,
  setCurrentPage,
  handleCloseModal,
  getTimer
}) => {
  const [selectedCards, setSelectedCards] = useState<number | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const drawPatterns: PatternsData = drawDetails && drawDetails.draw.prizes
  const { height, width } = useWindowSize()
  useEffect(() => {
    if (!ticketData?.count) {
      setSelectedCards(1)
    }
  }, [ticketData])

  const buyTicketHandler = async () => {
    if (selectedCards) {
      setIsLoading(true)
      try {
        const findWalletID = userWallet?.wallets.find(c => c.currency.code === 'USD')
        const getWalletId = findWalletID ? findWalletID.id : userWallet?.wallets[0].id
        const totalAmount = drawDetails.draw.basePrice * (selectedCards ?? 0)
        const response = await buyTicket(selectedCards, getWalletId, totalAmount)
        if (response) {

          onTicketPurchase()
        }
      } catch (error: any) {
        const errorName = error?.response?.data?.errors?.[0];
        if (errorName.name === "insufficientBalanceError") {
          handleCloseModal({
            status: true,
            test: errorName.description,
          });
        } else {
          handleCloseModal({
            status: true,
            test: errorName.description,
          });
        }
      }
    } else {
      if (ticketData && ticketData.count > 0) {
        setCurrentPage("gamePage")
      }
    }
  }

  useEffect(() => {
    if (getTimer && ticketData && ticketData.count === 0) {
      handleCloseModal({ status: true, test: "Ticket purchases are no longer available for this game" })
    }
  }, [getTimer, handleCloseModal, ticketData])
  const isIpad = width === 1010 && height >= 1300

  return (

    <div
      className="relative flex w-full min-h-screen flex-col  pb-6"
      style={{
        backgroundImage: `url('/assets/purchaseTicketBg.png')`,
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",


      }}
    >
      {/* Header - Title */}
      <div className="flex w-full justify-center flex-shrink-0 py-2 sm:py-3 md:py-2">
        <img
          src="/assets/selecrCardsText.png"
          alt="select_cards"
          className="w-[150px] sm:w-[180px] md:w-[220px] lg:w-[280px]"
          style={{
            ...(isIpad ? { width: '380px' } : {}), // Apply width and height for iPad
          }}
        />
      </div>

      {/* Middle Section - Controls */}
      <div className="w-full h- full flex-shrink-0 mt-2  ">
        <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-3 lg:gap-6 md:py-4 lg:py-2">
          <div className="flex h-full   flex-row  items-center justify-center gap-2 md:gap-3  ">
            <div className={`flex-shrink-0 h-full`}>
              <Timer isPurchase drawTime={time} />
            </div>

            <div
              className={`relative  flex-shrink-0 bg-contain bg-center bg-no-repeat py-[16px] px-[6px] md:px-[16px]
                    flex flex-col items-center justify-center`}
              style={{
                backgroundImage: `url('/assets/numOfTicktBg.png')`,
                ...(isIpad ? { width: '260px', height: '200px' } : {}),
              }}
            >
              <p className="text-white text-center text-sm sm:text-base font-bold mb-1"
                style={{ fontSize: isIpad ? '20px' : undefined, padding: isIpad ? '5px' : undefined }}>NUM CARDS</p>
              <div className="flex justify-around w-full px-4 md:px-6 gap-2">
                {[1, 2, 3, 4].map((num) => {
                  const purchasedCount = ticketData?.count || 0
                  const maxAllowed = 4
                  const remaining = maxAllowed - purchasedCount

                  const isDisabled = num > remaining
                  const tooltipText = `You've already purchased ${purchasedCount} ticket${purchasedCount > 1 ? "s" : ""}. Max allowed is ${maxAllowed}.`

                  return (
                    <div key={num} className="relative group">
                      <button
                        onClick={() => !isDisabled && setSelectedCards(num)}
                        disabled={isDisabled}
                        className={`${width <= 320 ? 'w-6 h-6' : ' w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10'}
                       flex items-center justify-center rounded-sm text-md  md:text-lg font-bold transition-colors
                     ${selectedCards === num ? "bg-blue-500" : "bg-[#321FA5]"} 
                     ${isDisabled ? "opacity-50 cursor-not-allowed" : "text-white"}
                     `}
                        style={{
                          ...(isIpad ? { width: '45px', height: '45px' } : {}), // Apply width and height for iPad
                        }}
                      >
                        {num}
                      </button>
                      {isDisabled && (
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-max px-2 py-1 bg-white text-black text-[10px] sm:text-[12px] rounded hidden group-hover:block z-10">
                          {tooltipText}

                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              <span className="text-[10px] md:text-[12px] lg:text-[14px] text-white mt-2 font-bold">Price per ticket : ${drawDetails.draw.game.basePrice}</span>
            </div>
          </div>

          <div className="flex-shrink-0 ">
            <button disabled={isLoading} className="focus:outline-none" onClick={buyTicketHandler}>
              <img
                src="/assets/playBtn.png"
                alt="Play Button"
                className="w-[160px] h-[50px]  md:w-[180px] md:h-[60px] lg:w-[250px] lg:h-[65px] object-contain"
                style={{
                  ...(isIpad ? { width: '220px', height: '160px' } : {}),
                }}
              />
            </button>
          </div>
        </div>
      </div>

      <div
        className="flex-grow w-full flex justify-center overflow-hidden mt-2 lg:mt-8 "
      >
        <div className="w-full h-full overflow-auto flex flex-col">
          <CreatePatterns drawPatterns={drawPatterns} />
        </div>
      </div>
    </div>
  )
}

export default PurchaseTicketsPage

